import { useEffect, useRef, useState } from 'react';

type Phase = 'idle' | 3 | 2 | 1 | 0 | 'done';

interface UseGameCountdownOptions {
  enabled: boolean;
  /** Passe le décompte (partie reprise) */
  skip?: boolean;
  onGo: () => void | Promise<void>;
}

/** Décompte 3 → 2 → 1 → 0 (Go !). Au 0 : dévoilement + onGo (démarrage chrono / run). */
export function useGameCountdown({ enabled, skip, onGo }: UseGameCountdownOptions) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [revealed, setRevealed] = useState(false);
  const onGoRef = useRef(onGo);
  onGoRef.current = onGo;

  useEffect(() => {
    if (skip) {
      setPhase('done');
      setRevealed(true);
      return;
    }
    if (!enabled) {
      setPhase('idle');
      setRevealed(false);
      return;
    }
    setPhase(3);
    setRevealed(false);
  }, [enabled, skip]);

  useEffect(() => {
    if (skip || !enabled || phase === 'done' || phase === 'idle') return;

    if (phase === 0) {
      setRevealed(true);
      Promise.resolve(onGoRef.current()).catch(() => {});
      const id = setTimeout(() => setPhase('done'), 600);
      return () => clearTimeout(id);
    }

    const id = setTimeout(() => setPhase((phase - 1) as Phase), 1000);
    return () => clearTimeout(id);
  }, [enabled, skip, phase]);

  const display = enabled && !skip && phase !== 'done' && phase !== 'idle' ? phase : null;
  const ready = revealed && phase === 'done';

  return { display, revealed, ready };
}
